import React from 'react';
import axios from 'axios';
import { Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { canCreateRecords, canEditRecords, NO_PERMISSION_TOOLTIP } from '../../services/permissions';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Autocomplete from '@mui/material/Autocomplete';
import { useSnackbar } from 'notistack';
import useToken from '../../services/token';
import DataEntryPager from './DataEntryPager';
// Точные значения "Вид операций" — как в Excel.
const OPERATION_TYPE_OPTIONS = [
    'Боронование',
    'Культивация 1',
    'Культивация 2',
    'Вспашка',
    'Посев подсолнечника',
    'Посев яровой пшеницы 104 га',
    'Прикатывание',
    'Уборка',
    'Уборка в свал',
    'Химическая обработка',
    'Удобрение',
];
const STATUS_LABELS = {
    planned: 'Запланирована',
    done: 'Выполнена',
};
const STATUS_CONFIG = {
    done: { label: 'Выполнена', color: '#2e7d32', bg: '#e8f5e9' },
    planned: { label: 'Запланирована', color: '#757575', bg: '#f0f0f0' },
};
const emptyForm = (defaultSeason) => ({
    season: defaultSeason,
    operationType: 'Боронование',
    name: '',
    volume: '',
    normSeed: '',
    normFertilizer: '',
    preparationName: '',
    yieldValue: '',
    equipment: '',
    date: '',
    cost: '',
    executor: null,
    status: 'planned',
});
const formatOperationType = (value) => value && value.trim() ? value : '—';
export default function TechCardEntryTable({ token, fieldId, fieldName }) {
    const { enqueueSnackbar } = useSnackbar();
    const { parseJwt } = useToken();
    const role = React.useMemo(() => { var _a, _b; return (_b = (_a = parseJwt(token)) === null || _a === void 0 ? void 0 : _a.role) !== null && _b !== void 0 ? _b : null; }, [token]);
    const canEdit = canEditRecords(role);
    const canCreate = canCreateRecords(role);
    const [rows, setRows] = React.useState([]);
    const [seasons, setSeasons] = React.useState([]);
    const [executors, setExecutors] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [loadError, setLoadError] = React.useState(false);
    const [paginationModel, setPaginationModel] = React.useState({ page: 0, pageSize: 25 });
    const [selectedIds, setSelectedIds] = React.useState([]);
    const selectedRow = React.useMemo(() => { var _a; return selectedIds.length === 1 ? (_a = rows.find(r => r.id === selectedIds[0])) !== null && _a !== void 0 ? _a : null : null; }, [selectedIds, rows]);
    const [addOpen, setAddOpen] = React.useState(false);
    const [addForm, setAddForm] = React.useState(emptyForm(''));
    const [editOpen, setEditOpen] = React.useState(false);
    const [editForm, setEditForm] = React.useState(emptyForm(''));
    const [saving, setSaving] = React.useState(false);
    const load = React.useCallback(() => {
        setLoading(true);
        setLoadError(false);
        const requests = [
            axios.get(`/techcard/list/${fieldId}`, { headers: { Authorization: `Bearer ${token}` } }),
            axios.get(`/field/season/list/${fieldId}`, { headers: { Authorization: `Bearer ${token}` } }),
        ];
        if (canEdit) {
            requests.push(axios.get('/user/executors', { headers: { Authorization: `Bearer ${token}` } }));
        }
        Promise.all(requests).then((results) => {
            var _a, _b, _c;
            setRows((_a = results[0].data) !== null && _a !== void 0 ? _a : []);
            setSeasons(((_b = results[1].data) !== null && _b !== void 0 ? _b : []).map((s) => ({
                id: s.id,
                season: s.season,
                culture_name: s.culture_name,
            })));
            if (canEdit && results[2]) {
                setExecutors((_c = results[2].data) !== null && _c !== void 0 ? _c : []);
            }
        }).catch(() => setLoadError(true)).finally(() => setLoading(false));
    }, [fieldId, token, canEdit]);
    React.useEffect(() => { load(); }, [load]);
    React.useEffect(() => { setSelectedIds([]); }, [rows.length === 0]);
    const cultureNameForSeason = (season) => { var _a, _b; return (_b = (_a = seasons.find(s => s.season === season)) === null || _a === void 0 ? void 0 : _a.culture_name) !== null && _b !== void 0 ? _b : '—'; };
    const executorName = (id) => { var _a, _b; return (_b = (_a = executors.find(e => e.id === id)) === null || _a === void 0 ? void 0 : _a.username) !== null && _b !== void 0 ? _b : (id ? `#${id}` : '—'); };
    const openAddDialog = () => {
        setAddForm(emptyForm(seasons.length > 0 ? seasons[0].season : ''));
        setAddOpen(true);
    };
    const createRow = () => {
        var _a, _b;
        if (!addForm.season || !addForm.name.trim())
            return;
        setSaving(true);
        axios.post('/techcard/create', {
            techcard_field_id: fieldId,
            techcard_season: addForm.season,
            techcard_culture_id: null,
            techcard_operation_type: addForm.operationType, // теперь точная строка из Excel
            techcard_operation_name: addForm.name.trim(),
            techcard_volume: addForm.volume || null,
            techcard_norm_seed: addForm.normSeed || null,
            techcard_norm_fertilizer: addForm.normFertilizer || null,
            techcard_preparation_name: addForm.preparationName || null,
            techcard_yield: addForm.yieldValue || null,
            techcard_equipment: addForm.equipment || null,
            techcard_date: addForm.date || null,
            techcard_cost: addForm.cost !== '' ? parseFloat(addForm.cost) : null,
            techcard_executor_id: (_b = (_a = addForm.executor) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null,
            techcard_is_done: addForm.status === 'done',
        }, { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }).then((response) => {
            var _a, _b;
            if ((_a = response.data) === null || _a === void 0 ? void 0 : _a.success) {
                enqueueSnackbar('Операция добавлена', { variant: 'success', autoHideDuration: 1500 });
                setAddOpen(false);
                load();
            }
            else {
                enqueueSnackbar(((_b = response.data) === null || _b === void 0 ? void 0 : _b.error) || 'Не удалось добавить операцию', { variant: 'error' });
            }
        }).catch((reason) => {
            var _a, _b;
            enqueueSnackbar(((_b = (_a = reason.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.detail) || reason.message, { variant: 'error' });
        }).finally(() => setSaving(false));
    };
    const openEditDialog = () => {
        var _a, _b, _c, _d, _f, _g, _h, _j, _k;
        if (!selectedRow)
            return;
        setEditForm({
            season: selectedRow.techcard_season,
            operationType: (_a = selectedRow.techcard_operation_type) !== null && _a !== void 0 ? _a : 'Боронование',
            name: selectedRow.techcard_operation_name,
            volume: (_b = selectedRow.techcard_volume) !== null && _b !== void 0 ? _b : '',
            normSeed: (_c = selectedRow.techcard_norm_seed) !== null && _c !== void 0 ? _c : '',
            normFertilizer: (_d = selectedRow.techcard_norm_fertilizer) !== null && _d !== void 0 ? _d : '',
            preparationName: (_f = selectedRow.techcard_preparation_name) !== null && _f !== void 0 ? _f : '',
            yieldValue: (_g = selectedRow.techcard_yield) !== null && _g !== void 0 ? _g : '',
            equipment: (_h = selectedRow.techcard_equipment) !== null && _h !== void 0 ? _h : '',
            date: (_j = selectedRow.techcard_date) !== null && _j !== void 0 ? _j : '',
            cost: selectedRow.techcard_cost !== null ? String(selectedRow.techcard_cost) : '',
            executor: (_k = executors.find(e => e.id === selectedRow.techcard_executor_id)) !== null && _k !== void 0 ? _k : null,
            status: selectedRow.status,
        });
        setEditOpen(true);
    };
    const saveEdit = () => {
        var _a, _b;
        if (!selectedRow)
            return;
        setSaving(true);
        axios.put(`/techcard/update/${selectedRow.id}`, {
            techcard_field_id: fieldId,
            techcard_season: selectedRow.techcard_season,
            techcard_culture_id: selectedRow.techcard_culture_id,
            techcard_operation_type: editForm.operationType, // точная строка из Excel
            techcard_operation_name: editForm.name.trim(),
            techcard_volume: editForm.volume || null,
            techcard_norm_seed: editForm.normSeed || null,
            techcard_norm_fertilizer: editForm.normFertilizer || null,
            techcard_preparation_name: editForm.preparationName || null,
            techcard_yield: editForm.yieldValue || null,
            techcard_equipment: editForm.equipment || null,
            techcard_date: editForm.date || null,
            techcard_cost: editForm.cost !== '' ? parseFloat(editForm.cost) : null,
            techcard_executor_id: (_b = (_a = editForm.executor) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null,
            techcard_is_done: editForm.status === 'done',
        }, { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }).then((response) => {
            var _a, _b;
            if ((_a = response.data) === null || _a === void 0 ? void 0 : _a.success) {
                enqueueSnackbar('Изменения сохранены', { variant: 'success', autoHideDuration: 1500 });
                setEditOpen(false);
                setSelectedIds([]);
                load();
            }
            else {
                enqueueSnackbar(((_b = response.data) === null || _b === void 0 ? void 0 : _b.error) || 'Недостаточно прав для редактирования', { variant: 'error' });
            }
        }).catch(() => {
            enqueueSnackbar('Ошибка сохранения', { variant: 'error' });
        }).finally(() => setSaving(false));
    };
    const deleteSelected = () => {
        if (selectedIds.length === 0)
            return;
        Promise.all(selectedIds.map((id) => axios.delete(`/techcard/delete/${id}`, { headers: { Authorization: `Bearer ${token}` } }))).then((responses) => {
            const failed = responses.filter((r) => { var _a; return !((_a = r.data) === null || _a === void 0 ? void 0 : _a.success); }).length;
            const succeeded = responses.length - failed;
            if (succeeded > 0) {
                enqueueSnackbar(`Удалено записей: ${succeeded}`, { variant: 'success', autoHideDuration: 1500 });
            }
            if (failed > 0) {
                enqueueSnackbar(`Не удалось удалить: ${failed} (недостаточно прав)`, { variant: 'error' });
            }
            setSelectedIds([]);
            load();
        }).catch(() => {
            enqueueSnackbar('Ошибка удаления', { variant: 'error' });
            setSelectedIds([]);
            load();
        });
    };
    const columns = [
        {
            field: 'status',
            headerName: 'Статус',
            width: 150,
            renderCell: (params) => {
                var _a;
                const cfg = (_a = STATUS_CONFIG[params.value]) !== null && _a !== void 0 ? _a : STATUS_CONFIG.planned;
                return React.createElement(Chip, { label: cfg.label, size: "small", sx: { color: cfg.color, backgroundColor: cfg.bg, fontWeight: 600 } });
            },
        },
        { field: 'techcard_season', headerName: 'Сезон', width: 90 },
        {
            field: 'culture',
            headerName: 'Культура',
            width: 120,
            valueGetter: (_value, row) => cultureNameForSeason(row.techcard_season),
        },
        {
            field: 'techcard_operation_type',
            headerName: 'Вид операций',
            width: 220,
            valueFormatter: (value) => formatOperationType(value),
        },
        { field: 'techcard_operation_name', headerName: 'Наименование операции', flex: 1, minWidth: 180 },
        { field: 'techcard_date', headerName: 'Дата', width: 170 },
        { field: 'techcard_norm_seed', headerName: 'Норма высева', width: 130 },
        { field: 'techcard_norm_fertilizer', headerName: 'Норма удобрения', width: 140 },
        { field: 'techcard_volume', headerName: 'Объём (глуб. высота)', width: 140 },
        { field: 'techcard_preparation_name', headerName: 'Препарат', width: 130 },
        { field: 'techcard_equipment', headerName: 'Техника', width: 120 },
        { field: 'techcard_cost', headerName: 'Затраты', width: 90 },
        { field: 'techcard_yield', headerName: 'Урожайность', width: 110 },
        {
            field: 'techcard_executor_id',
            headerName: 'Исполнитель',
            width: 150,
            valueFormatter: (value) => executorName(value),
        },
    ];
    const sortedRows = [...rows].sort((a, b) => b.techcard_season - a.techcard_season);
    React.useEffect(() => {
        const lastPage = Math.max(0, Math.ceil(sortedRows.length / paginationModel.pageSize) - 1);
        if (paginationModel.page > lastPage) {
            setPaginationModel((current) => (Object.assign(Object.assign({}, current), { page: lastPage })));
        }
    }, [sortedRows.length, paginationModel.page, paginationModel.pageSize]);
    const renderOperationFields = (form, setForm, seasonEditable) => {
        return (React.createElement(Stack, { spacing: 2, sx: { mt: 1 } },
            React.createElement(Stack, { direction: "row", spacing: 2 },
                React.createElement(TextField, { select: true, label: "\u0421\u0435\u0437\u043E\u043D", variant: "filled", fullWidth: true, value: form.season, disabled: !seasonEditable, onChange: (e) => setForm(Object.assign(Object.assign({}, form), { season: Number(e.target.value) })) }, seasons.map(s => (React.createElement(MenuItem, { key: s.season, value: s.season },
                    "\u0421\u0435\u0437\u043E\u043D ",
                    s.season)))),
                React.createElement(TextField, { select: true, label: "\u0412\u0438\u0434 \u043E\u043F\u0435\u0440\u0430\u0446\u0438\u0439", variant: "filled", fullWidth: true, value: form.operationType, onChange: (e) => setForm(Object.assign(Object.assign({}, form), { operationType: e.target.value })) }, OPERATION_TYPE_OPTIONS.map((o) => (React.createElement(MenuItem, { key: o, value: o }, o))))),
            React.createElement(TextField, { select: true, label: "\u0421\u0442\u0430\u0442\u0443\u0441", variant: "filled", fullWidth: true, value: form.status, onChange: (e) => setForm(Object.assign(Object.assign({}, form), { status: e.target.value })) },
                React.createElement(MenuItem, { value: "planned" }, STATUS_LABELS.planned),
                React.createElement(MenuItem, { value: "done" }, STATUS_LABELS.done)),
            React.createElement(TextField, { label: "\u041D\u0430\u0438\u043C\u0435\u043D\u043E\u0432\u0430\u043D\u0438\u0435 \u043E\u043F\u0435\u0440\u0430\u0446\u0438\u0438", variant: "filled", fullWidth: true, required: true, value: form.name, onChange: (e) => setForm(Object.assign(Object.assign({}, form), { name: e.target.value })) }),
            React.createElement(TextField, { label: "\u0414\u0430\u0442\u0430", variant: "filled", fullWidth: true, placeholder: "\u043D\u0430\u043F\u0440. 15.04, 08.05\u201309.05, \u0438\u043B\u0438 02.05, 03.05", value: form.date, onChange: (e) => setForm(Object.assign(Object.assign({}, form), { date: e.target.value })) }),
            React.createElement(Stack, { direction: "row", spacing: 2 },
                React.createElement(TextField, { label: "\u041D\u043E\u0440\u043C\u0430 \u0432\u044B\u0441\u0435\u0432\u0430", variant: "filled", fullWidth: true, placeholder: "\u043D\u0430\u043F\u0440. 220 \u043A\u043C/\u0433\u0430", value: form.normSeed, onChange: (e) => setForm(Object.assign(Object.assign({}, form), { normSeed: e.target.value })) }),
                React.createElement(TextField, { label: "\u041D\u043E\u0440\u043C\u0430 \u0432\u043D\u0435\u0441\u0435\u043D\u0438\u044F \u0443\u0434\u043E\u0431\u0440\u0435\u043D\u0438\u044F", variant: "filled", fullWidth: true, value: form.normFertilizer, onChange: (e) => setForm(Object.assign(Object.assign({}, form), { normFertilizer: e.target.value })) })),
            React.createElement(Stack, { direction: "row", spacing: 2 },
                React.createElement(TextField, { label: "\u041E\u0431\u044A\u0451\u043C (\u0433\u043B\u0443\u0431\u0438\u043D\u0430/\u0432\u044B\u0441\u043E\u0442\u0430)", variant: "filled", fullWidth: true, placeholder: "\u043D\u0430\u043F\u0440. 25 \u0441\u043C, 103 \u0433\u0430", value: form.volume, onChange: (e) => setForm(Object.assign(Object.assign({}, form), { volume: e.target.value })) }),
                React.createElement(TextField, { label: "\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435 \u043F\u0440\u0435\u043F\u0430\u0440\u0430\u0442\u0430", variant: "filled", fullWidth: true, placeholder: "\u043D\u0430\u043F\u0440. \u0413\u0435\u0440\u0431\u0438\u0446\u0438\u0434+", value: form.preparationName, onChange: (e) => setForm(Object.assign(Object.assign({}, form), { preparationName: e.target.value })) })),
            React.createElement(Stack, { direction: "row", spacing: 2 },
                React.createElement(TextField, { label: "\u0422\u0435\u0445\u043D\u0438\u043A\u0430", variant: "filled", fullWidth: true, value: form.equipment, onChange: (e) => setForm(Object.assign(Object.assign({}, form), { equipment: e.target.value })) }),
                React.createElement(TextField, { label: "\u0417\u0430\u0442\u0440\u0430\u0442\u044B", variant: "filled", type: "number", fullWidth: true, value: form.cost, onChange: (e) => setForm(Object.assign(Object.assign({}, form), { cost: e.target.value })) })),
            React.createElement(TextField, { label: "\u0423\u0440\u043E\u0436\u0430\u0439\u043D\u043E\u0441\u0442\u044C", variant: "filled", fullWidth: true, placeholder: "\u043D\u0430\u043F\u0440. 44 \u0446/\u0433\u0430 \u2014 \u0437\u0430\u043F\u043E\u043B\u043D\u044F\u0435\u0442\u0441\u044F \u0443 \u043E\u043F\u0435\u0440\u0430\u0446\u0438\u0439 \u0443\u0431\u043E\u0440\u043A\u0438", value: form.yieldValue, onChange: (e) => setForm(Object.assign(Object.assign({}, form), { yieldValue: e.target.value })) }),
            React.createElement(Autocomplete, { options: executors, getOptionLabel: (o) => o.username, value: form.executor, onChange: (_e, v) => setForm(Object.assign(Object.assign({}, form), { executor: v })), renderInput: (params) => (React.createElement(TextField, Object.assign({}, params, { variant: "filled", label: "\u0418\u0441\u043F\u043E\u043B\u043D\u0438\u0442\u0435\u043B\u044C (\u043D\u0435\u043E\u0431\u044F\u0437\u0430\u0442\u0435\u043B\u044C\u043D\u043E)" }))) })));
    };
    return (React.createElement(Box, { sx: { height: '100%', width: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0, overflow: 'hidden', bgcolor: '#f7f9f7' } },
        React.createElement(Box, { component: "header", sx: { px: { xs: 2, md: 3 }, py: 2, flexShrink: 0, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' } },
            React.createElement(Stack, { direction: { xs: 'column', lg: 'row' }, justifyContent: "space-between", alignItems: { xs: 'stretch', lg: 'center' }, spacing: 1.5 },
                React.createElement(Stack, { direction: "row", spacing: 1, alignItems: "center", flexWrap: "wrap", useFlexGap: true },
                    React.createElement(Typography, { variant: "body2", color: "text.secondary", noWrap: true, title: fieldName, sx: { maxWidth: { xs: 140, md: 220 } } }, fieldName),
                    React.createElement(Typography, { variant: "h6", component: "h2", fontWeight: 600 }, "\u0422\u0435\u0445\u043D\u043E\u043B\u043E\u0433\u0438\u0447\u0435\u0441\u043A\u0430\u044F \u043A\u0430\u0440\u0442\u0430"),
                    React.createElement(Chip, { size: "small", variant: "outlined", label: `${sortedRows.length} ${sortedRows.length === 1 ? 'операция' : 'операций'}` })),
                React.createElement(Box, { sx: { display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', lg: 'flex-end' }, flexShrink: 0 } },
                    React.createElement(Tooltip, { title: canEdit ? '' : NO_PERMISSION_TOOLTIP },
                        React.createElement("span", null,
                            React.createElement(Button, { startIcon: React.createElement(EditIcon, null), variant: "outlined", disabled: !canEdit || !selectedRow, onClick: openEditDialog }, "\u0420\u0435\u0434\u0430\u043A\u0442\u0438\u0440\u043E\u0432\u0430\u0442\u044C"))),
                    React.createElement(Tooltip, { title: canEdit ? '' : NO_PERMISSION_TOOLTIP },
                        React.createElement("span", null,
                            React.createElement(Button, { startIcon: React.createElement(DeleteIcon, null), variant: "outlined", color: "error", disabled: !canEdit || selectedIds.length === 0, onClick: deleteSelected },
                                "\u0423\u0434\u0430\u043B\u0438\u0442\u044C",
                                selectedIds.length > 1 ? ` (${selectedIds.length})` : ''))),
                    React.createElement(Tooltip, { title: canCreate ? '' : NO_PERMISSION_TOOLTIP },
                        React.createElement("span", null,
                            React.createElement(Button, { "data-onboarding-anchor": "tech-card-add", startIcon: React.createElement(AddIcon, null), variant: "contained", onClick: openAddDialog, disabled: !canCreate || seasons.length === 0 }, "\u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C \u043E\u043F\u0435\u0440\u0430\u0446\u0438\u044E")))))),
        React.createElement(Box, { sx: { flex: 1, minHeight: 0, minWidth: 0, p: { xs: 1.5, md: 2 } } },
            React.createElement(Box, { "data-onboarding-anchor": "tech-card-entries", "data-onboarding-state": loading ? 'loading' : loadError ? 'error' : sortedRows.length === 0 ? 'empty' : undefined, sx: { height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden', border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' } },
                loading ? (React.createElement(Stack, { alignItems: "center", justifyContent: "center", spacing: 1.5, sx: { flex: 1, minHeight: 0 } },
                    React.createElement(CircularProgress, { size: 28 }),
                    React.createElement(Typography, { variant: "body2", color: "text.secondary" }, "\u0417\u0430\u0433\u0440\u0443\u0436\u0430\u0435\u043C \u043E\u043F\u0435\u0440\u0430\u0446\u0438\u0438\u2026"))) : loadError ? (React.createElement(Stack, { alignItems: "center", justifyContent: "center", spacing: 1, sx: { flex: 1, minHeight: 0, px: 3, textAlign: 'center' } },
                    React.createElement(Typography, { variant: "body2", color: "error" }, "\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u0437\u0430\u0433\u0440\u0443\u0437\u0438\u0442\u044C \u043E\u043F\u0435\u0440\u0430\u0446\u0438\u0438."),
                    React.createElement(Button, { size: "small", onClick: load }, "\u041F\u043E\u0432\u0442\u043E\u0440\u0438\u0442\u044C"))) : sortedRows.length === 0 ? (React.createElement(Stack, { alignItems: "center", justifyContent: "center", spacing: 1, sx: { flex: 1, minHeight: 0, px: 3, textAlign: 'center' } },
                    React.createElement(Typography, { variant: "h6" }, "\u041E\u043F\u0435\u0440\u0430\u0446\u0438\u0439 \u043F\u043E\u043A\u0430 \u043D\u0435\u0442"),
                    React.createElement(Typography, { variant: "body2", color: "text.secondary" }, seasons.length === 0
                        ? 'Сначала добавьте сезон на странице «Опытные участки», затем можно будет внести операции.'
                        : 'Добавьте первую операцию кнопкой «Добавить операцию» в верхней панели.'))) : (React.createElement(DataGrid, { rows: sortedRows, columns: columns, checkboxSelection: canEdit, disableColumnMenu: true, disableRowSelectionOnClick: true, hideFooter: true, paginationModel: paginationModel, onPaginationModelChange: setPaginationModel, rowSelectionModel: { type: 'include', ids: new Set(selectedIds) }, onRowSelectionModelChange: (newSelection) => {
                        var _a;
                        const ids = Array.isArray(newSelection) ? newSelection : Array.from((_a = newSelection === null || newSelection === void 0 ? void 0 : newSelection.ids) !== null && _a !== void 0 ? _a : []);
                        setSelectedIds(ids.map((id) => Number(id)));
                    }, sx: {
                        flex: 1,
                        minHeight: 0,
                        border: 0,
                        '& .MuiDataGrid-columnHeaders': { bgcolor: '#f6f8f6', borderBottom: 1, borderColor: 'divider' },
                        '& .MuiDataGrid-row:hover': { bgcolor: 'rgba(18, 113, 71, 0.05)' },
                        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': { outline: 'none' },
                    } })),
                React.createElement(DataEntryPager, { total: sortedRows.length, page: paginationModel.page, pageSize: paginationModel.pageSize, onPageChange: (page) => setPaginationModel((current) => (Object.assign(Object.assign({}, current), { page }))), onPageSizeChange: (pageSize) => setPaginationModel({ page: 0, pageSize }) }))),
        React.createElement(Dialog, { open: addOpen, onClose: () => setAddOpen(false), maxWidth: "sm", fullWidth: true },
            React.createElement(DialogTitle, null, "\u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C \u043E\u043F\u0435\u0440\u0430\u0446\u0438\u044E"),
            React.createElement(DialogContent, null, renderOperationFields(addForm, setAddForm, true)),
            React.createElement(DialogActions, null,
                React.createElement(Button, { disabled: !addForm.season || !addForm.name.trim() || saving, onClick: createRow }, "\u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C"),
                React.createElement(Button, { disabled: saving, onClick: () => setAddOpen(false) }, "\u041E\u0442\u043C\u0435\u043D\u0430"))),
        React.createElement(Dialog, { open: editOpen, onClose: () => setEditOpen(false), maxWidth: "sm", fullWidth: true },
            React.createElement(DialogTitle, null, "\u0420\u0435\u0434\u0430\u043A\u0442\u0438\u0440\u043E\u0432\u0430\u0442\u044C \u043E\u043F\u0435\u0440\u0430\u0446\u0438\u044E"),
            React.createElement(DialogContent, null, renderOperationFields(editForm, setEditForm, false)),
            React.createElement(DialogActions, null,
                React.createElement(Button, { disabled: saving, onClick: saveEdit }, "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C"),
                React.createElement(Button, { disabled: saving, onClick: () => setEditOpen(false) }, "\u041E\u0442\u043C\u0435\u043D\u0430")))));
}
